import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useTranslations } from 'use-intl';
import type { Locale } from '@/i18n';
import { localePath, swapLocale } from '@/app/locale';
import { LOCALES } from '@/i18n';
import { useLocation } from 'react-router';

/**
 * The signed-out frame. One column, no chrome: the only job here is to get a
 * coach into their season, so nothing competes with the form.
 */
export function AuthLayout({
  locale,
  title,
  lead,
  children,
  footer,
}: {
  locale: Locale;
  title: string;
  lead?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const t = useTranslations();
  const { pathname } = useLocation();

  return (
    <div className="min-h-dvh px-4 py-10 sm:py-16">
      <div className="mx-auto w-full max-w-[26rem]">
        <Link
          to={localePath(locale, '/')}
          className="font-condensed text-subhead font-bold tracking-tight text-ink"
        >
          {t('app.name')}
        </Link>
        <p className="mt-0.5 text-meta text-ink-faint">{t('app.tagline')}</p>

        <div className="mt-8 rounded-(--radius-panel) border border-line bg-surface p-5 sm:p-7">
          <h1 className="font-condensed text-section font-semibold leading-tight tracking-tight">
            {title}
          </h1>
          {lead ? <p className="mt-1.5 text-meta text-ink-muted">{lead}</p> : null}
          <div className="mt-6">{children}</div>
        </div>

        {footer ? <div className="mt-5 text-center text-meta">{footer}</div> : null}

        <nav aria-label={t('nav.language')} className="mt-8 flex justify-center gap-1">
          {LOCALES.map((l) => (
            <Link
              key={l}
              to={swapLocale(pathname, l)}
              aria-current={l === locale ? 'page' : undefined}
              className={`rounded-(--radius-control) px-2.5 py-1 text-meta transition-colors ${
                l === locale
                  ? 'bg-ink text-paper'
                  : 'text-ink-muted hover:bg-surface hover:text-ink'
              }`}
            >
              {t(`locale.${l}`)}
            </Link>
          ))}
        </nav>
      </div>
    </div>
  );
}
